import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { resolveTenantUrlParam } from '@/lib/tenant-url.js';

import { buildMockAck, isRunningStandalone } from './mock-host.js';
import {
  CONNECTION_EVENTS,
  type DesignTokens,
  type ExtensionDetails,
  type HandshakeAckPayload,
  describeHandshakeAckFailure,
  parseHandshakeAck,
  parseTokenRefresh,
} from './protocol.js';

export type HandshakeStatus = 'connecting' | 'connected' | 'error';

export interface HandshakeState {
  status: HandshakeStatus;
  error: string | null;
  jwt: string | null;
  expires: string | null;
  extensionDetails: ExtensionDetails | null;
  designTokens: DesignTokens | null;
  /** Origin of the Kadanza host; the only allowed `postMessage` target. */
  hostOrigin: string | null;
  locale: string | null;
  /**
   * Asks the host to mint a fresh token, resolving with it once it arrives.
   *
   * Awaitable so that a caller which hit a 401 can retry rather than report a
   * failure: the host refreshes on demand, so an expired token is the ordinary
   * end of a token's life, not an error worth showing anyone.
   */
  requestTokenRefresh: () => Promise<string | null>;
}

/**
 * The host may have to renew its own session before it can mint ours, which is
 * a round trip of its own, so this is generous. It only bounds the wait for a
 * host that never answers.
 */
const TOKEN_REFRESH_TIMEOUT_MS = 15_000;

function logHandshake(message: string): void {
  console.log(`[handshake] ${message}`);
}

/** Reduces `tenantUrl` to a bare origin, rejecting anything that is not an http(s) URL. */
function validateTenantUrl(value: string | null): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    return url.origin;
  } catch {
    return null;
  }
}

/**
 * Implements the Kadanza Extension Page handshake:
 *
 * 1. Read `tenantUrl` and derive the single allowed origin from it.
 * 2. Post `HANDSHAKE_INIT` to the parent frame.
 * 3. Accept `HANDSHAKE_ACK` and `TOKEN_REFRESH`, but only from that origin.
 *
 * The tenant identity in the ACK is display context only. Authorization is
 * decided by the API from the JWT, which is why a spoofed `tenantDomain` here
 * would achieve nothing.
 */
export function useHandshake(): HandshakeState {
  const [payload, setPayload] = useState<HandshakeAckPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  const tenantUrl = useMemo(() => resolveTenantUrlParam(), []);
  const allowedOrigin = useMemo(() => validateTenantUrl(tenantUrl), [tenantUrl]);

  const allowedOriginRef = useRef<string | null>(allowedOrigin);
  allowedOriginRef.current = allowedOrigin;
  const payloadRef = useRef<HandshakeAckPayload | null>(null);
  payloadRef.current = payload;

  const postInit = useCallback(() => {
    const origin = allowedOriginRef.current;
    if (!origin || window.parent === window) return;
    try {
      window.parent.postMessage({ type: CONNECTION_EVENTS.handshakeInit }, origin);
    } catch (e) {
      console.warn('[handshake] failed to send HANDSHAKE_INIT', e);
    }
  }, []);

  const waitersRef = useRef<Array<(token: string | null) => void>>([]);
  const inFlightRef = useRef<Promise<string | null> | null>(null);
  const timeoutRef = useRef<number | null>(null);

  /** Resolves everyone waiting on the current refresh and clears the slot. */
  const settleRefresh = useCallback((token: string | null) => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    inFlightRef.current = null;
    const waiters = waitersRef.current;
    waitersRef.current = [];
    for (const resolve of waiters) resolve(token);
  }, []);

  /**
   * Concurrent callers share one request. A page load fires several queries at
   * once, so without this a single expiry would post a refresh per request and
   * the host would mint a token for each.
   */
  const requestTokenRefresh = useCallback((): Promise<string | null> => {
    const origin = allowedOriginRef.current;
    if (!origin || window.parent === window) return Promise.resolve(null);
    if (inFlightRef.current) return inFlightRef.current;

    const pending = new Promise<string | null>((resolve) => {
      waitersRef.current.push(resolve);
    });
    inFlightRef.current = pending;
    timeoutRef.current = window.setTimeout(() => {
      console.warn('[handshake] token refresh timed out');
      settleRefresh(null);
    }, TOKEN_REFRESH_TIMEOUT_MS);

    try {
      window.parent.postMessage({ type: CONNECTION_EVENTS.requestTokenRefresh }, origin);
    } catch (e) {
      console.warn('[handshake] failed to request token refresh', e);
      settleRefresh(null);
    }

    return pending;
  }, [settleRefresh]);

  useEffect(() => {
    if (isRunningStandalone()) {
      setPayload(buildMockAck());
      logHandshake('connected (standalone dev mock)');
      return;
    }

    if (!allowedOrigin) {
      const msg = 'Missing or invalid `tenantUrl` query parameter.';
      console.warn(`[handshake] failed — ${msg}`);
      setError(msg);
      return;
    }

    logHandshake(`waiting for host (${allowedOrigin})…`);

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== allowedOrigin) return;

      const msgType =
        event.data && typeof event.data === 'object'
          ? (event.data as { type?: unknown }).type
          : undefined;

      if (msgType === CONNECTION_EVENTS.handshakeAck) {
        const ack = parseHandshakeAck(event.data);
        if (ack) {
          const { tenantDomain, tenantId } = ack.payload.extensionDetails;
          logHandshake(`connected (tenant ${tenantDomain || tenantId})`);
          setPayload(ack.payload);
          setError(null);
          // Some hosts answer a refresh with a fresh ACK rather than a
          // TOKEN_REFRESH, which would otherwise leave callers waiting.
          settleRefresh(ack.payload.authToken.jwt);
          return;
        }
        const reasons = describeHandshakeAckFailure(event.data);
        console.warn(
          `[handshake] failed — invalid HANDSHAKE_ACK${reasons.length ? `: ${reasons.join('; ')}` : ''}`,
        );
        return;
      }

      if (msgType === CONNECTION_EVENTS.tokenRefresh) {
        const refresh = parseTokenRefresh(event.data);
        if (refresh) {
          logHandshake('token refreshed');
          setPayload((prev) => (prev ? { ...prev, authToken: refresh.payload.authToken } : prev));
          settleRefresh(refresh.payload.authToken.jwt);
          return;
        }
        console.warn('[handshake] failed — invalid TOKEN_REFRESH');
        settleRefresh(null);
      }
    };

    window.addEventListener('message', handleMessage);
    postInit();

    // A backgrounded iframe can miss the ACK; retry when it becomes visible again.
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && !payloadRef.current) postInit();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.removeEventListener('message', handleMessage);
      document.removeEventListener('visibilitychange', onVisibility);
      // Nothing will answer once the listener is gone.
      settleRefresh(null);
    };
  }, [allowedOrigin, postInit, settleRefresh]);

  const status: HandshakeStatus = payload ? 'connected' : error ? 'error' : 'connecting';

  return {
    status,
    error,
    jwt: payload?.authToken.jwt ?? null,
    expires: payload?.authToken.expires ?? null,
    extensionDetails: payload?.extensionDetails ?? null,
    designTokens: payload?.designTokens ?? null,
    hostOrigin: allowedOrigin,
    locale: payload?.extensionDetails.locale ?? null,
    requestTokenRefresh,
  };
}
